import { useEffect } from 'react';

/**
 * Locks all scroll containers when a modal/overlay is visible.
 * Targets body, .main-content, and adds non-passive touchmove
 * prevention for iOS WKWebView.
 * Counter handles nested modals correctly.
 */
let lockCount = 0;
let savedScrollTop = 0;
let touchMoveHandler = null;

function handleTouchMove(e) {
    // Find if the touch target is inside a scrollable modal content
    const scrollable = e.target.closest('[data-modal-scrollable]');
    if (!scrollable) {
        // Touch is on the overlay background — block it
        e.preventDefault();
        return;
    }

    // Allow scrolling inside modal content, but prevent overscroll
    const { scrollTop, scrollHeight, clientHeight } = scrollable;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    if (scrollHeight <= clientHeight) {
        // Content doesn't overflow — no scrolling needed
        e.preventDefault();
        return;
    }

    // Detect scroll direction from touch
    const touchY = e.touches[0].clientY;
    const lastTouchY = scrollable._lastTouchY;
    scrollable._lastTouchY = touchY;

    if (lastTouchY === undefined) return; // first touch, skip

    const movingDown = touchY > lastTouchY; // finger moves down = scroll up
    const movingUp = touchY < lastTouchY;   // finger moves up = scroll down

    if ((atTop && movingDown) || (atBottom && movingUp)) {
        e.preventDefault();
    }
}

function handleTouchStart(e) {
    const scrollable = e.target.closest('[data-modal-scrollable]');
    if (scrollable) {
        scrollable._lastTouchY = e.touches[0].clientY;
    }
}

export default function useBodyScrollLock(isLocked) {
    useEffect(() => {
        if (!isLocked) return;

        if (lockCount === 0) {
            const mainContent = document.querySelector('.main-content');
            if (mainContent) {
                savedScrollTop = mainContent.scrollTop;
            }
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
            document.body.style.top = '0';
            if (mainContent) {
                mainContent.style.overflow = 'hidden';
                mainContent.style.touchAction = 'none';
            }

            // Non-passive touchmove to prevent iOS WKWebView scrolling
            document.addEventListener('touchstart', handleTouchStart, { passive: true });
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
        }
        lockCount++;

        return () => {
            lockCount--;
            if (lockCount === 0) {
                const mainContent = document.querySelector('.main-content');
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
                document.body.style.height = '';
                document.body.style.top = '';
                if (mainContent) {
                    mainContent.style.overflow = '';
                    mainContent.style.touchAction = '';
                    mainContent.scrollTop = savedScrollTop;
                }

                document.removeEventListener('touchstart', handleTouchStart);
                document.removeEventListener('touchmove', handleTouchMove);
            }
        };
    }, [isLocked]);
}
